const isProd = import.meta.env.PROD;

/**
 * Thin logger utility.
 * - In non-production: logs to the console.
 * - In production: errors are sent to Airbrake; other levels are silent.
 *
 * Airbrake is loaded lazily to avoid circular dependency
 * (configureStore → logger → ErrorBoundary → react-redux → store).
 */
let _airbrake: { notify: (opts: any) => void } | null = null;

async function getAirbrake() {
  if (!_airbrake) {
    try {
      // Lazy dynamic import to break the circular import chain
      const { airbrake } = await import("components/ErrorBoundary");
      _airbrake = airbrake;
    } catch {
      // ErrorBoundary not yet loaded — swallow
    }
  }
  return _airbrake;
}

const logger = {
  async error(...args: unknown[]) {
    if (!isProd) {
      console.error("[ERROR]", ...args);
    }
    try {
      const airbrake = await getAirbrake();
      airbrake?.notify({
        error: args[0] instanceof Error ? args[0] : new Error(String(args[0])),
        params: { details: args.slice(1) },
      });
    } catch {
      // Airbrake unavailable — swallow
    }
  },

  warn(...args: unknown[]) {
    if (!isProd) {
      console.warn("[WARN]", ...args);
    }
  },

  info(...args: unknown[]) {
    if (!isProd) {
      console.log("[INFO]", ...args);
    }
  },

  debug(...args: unknown[]) {
    if (!isProd) {
      console.log("[DEBUG]", ...args);
    }
  },
};

export default logger;
